import React from "react";
import { Player, Inning } from "../../matchTypes";
import { getBowlerStats } from "../../engine/MatchEngine";

interface CurrentPlayersCardProps {
  striker: Player | undefined;
  nonStriker: Player | undefined;
  bowler: Player | undefined;
  inning: Inning;
  ballsPerOver: number;
  onChangeStriker: () => void;
  onChangeNonStriker: () => void;
  onChangeBowler: () => void;
  waitingForNewBatsman?: boolean;
  waitingForNewBowler?: boolean;
  onSelectNewBatsman?: () => void;
  onSelectNewBowler?: () => void;
  onSelectNonStriker?: () => void;
  tossSet?: boolean;
  onRetireHurt?: () => void;
  onUndoPlayerChange?: () => void;
  canUndoPlayerChange?: boolean;
}

const CurrentPlayersCard: React.FC<CurrentPlayersCardProps> = ({
  striker,
  nonStriker,
  bowler,
  inning,
  ballsPerOver,
  onChangeStriker,
  onChangeNonStriker,
  onChangeBowler,
  waitingForNewBatsman = false,
  waitingForNewBowler = false,
  onSelectNewBatsman,
  onSelectNewBowler,
  onSelectNonStriker,
  tossSet = true,
  onRetireHurt,
  onUndoPlayerChange,
  canUndoPlayerChange = false,
}) => {
  if (!tossSet) {
    return (
      <div className="v222-players">
        <div className="v222-players-head">
          <span className="v222-dot" />
          <span className="v222-section-label">Current Players</span>
        </div>
        <div className="v222-empty">Complete toss to start match</div>
        <Styles />
      </div>
    );
  }

  return (
    <div className="v222-players">
      <div className="v222-players-head">
        <span className="v222-dot" />
        <span className="v222-section-label">Current Players</span>
        {canUndoPlayerChange && onUndoPlayerChange && (
          <button onClick={onUndoPlayerChange} className="v222-undo-change">Undo Change</button>
        )}
      </div>

      {/* Striker */}
      <div className={`v222-player-row v222-striker-row ${striker ? "" : "v222-slot-empty"}`}>
        <div className="v222-player-label">
          <span className="v222-striker-icon">●</span>
          <span className="v222-striker-tag">Striker</span>
        </div>
        {striker ? (
          <div className="v222-player-info">
            <div className="v222-player-name-row">
              <span className="v222-player-name">{striker.name}</span>
              {striker.isCaptain && <span className="v222-badge v222-badge-c">C</span>}
              {striker.isKeeper && <span className="v222-badge v222-badge-wk">WK</span>}
              <div className="v222-player-actions">
                {onRetireHurt && (
                  <button onClick={onRetireHurt} className="v222-link-btn v222-retire-btn">Retire</button>
                )}
                <button onClick={onChangeStriker} className="v222-link-btn">Change</button>
              </div>
            </div>
            <div className="v222-player-stats">
              <span><b>{striker.runs}</b><i>R</i></span>
              <span><b>{striker.balls}</b><i>B</i></span>
              <span><b>{striker.fours}</b><i>4s</i></span>
              <span><b>{striker.sixes}</b><i>6s</i></span>
              <span><b>{striker.balls > 0 ? ((striker.runs / striker.balls) * 100).toFixed(1) : "0.0"}</b><i>SR</i></span>
            </div>
          </div>
        ) : (
          <button onClick={onSelectNewBatsman} className="v222-select-btn v222-select-striker">
            <span>Select New Batsman</span>
            <span className="v222-arrow">→</span>
          </button>
        )}
      </div>

      {/* Non-Striker */}
      <div className={`v222-player-row v222-nonstriker-row ${nonStriker ? "" : "v222-slot-empty"}`}>
        <div className="v222-player-label">
          <span className="v222-nonstriker-icon">○</span>
          <span className="v222-nonstriker-tag">Non-Striker</span>
        </div>
        {nonStriker ? (
          <div className="v222-player-info">
            <div className="v222-player-name-row">
              <span className="v222-player-name">{nonStriker.name}</span>
              {nonStriker.isCaptain && <span className="v222-badge v222-badge-c">C</span>}
              {nonStriker.isKeeper && <span className="v222-badge v222-badge-wk">WK</span>}
              <div className="v222-player-actions">
                <button
                  onClick={() => { onSelectNonStriker ? onSelectNonStriker() : onChangeNonStriker(); }}
                  className="v222-link-btn"
                >Change</button>
              </div>
            </div>
            <div className="v222-player-stats">
              <span><b>{nonStriker.runs}</b><i>R</i></span>
              <span><b>{nonStriker.balls}</b><i>B</i></span>
              <span><b>{nonStriker.fours}</b><i>4s</i></span>
              <span><b>{nonStriker.sixes}</b><i>6s</i></span>
              <span><b>{nonStriker.balls > 0 ? ((nonStriker.runs / nonStriker.balls) * 100).toFixed(1) : "0.0"}</b><i>SR</i></span>
            </div>
          </div>
        ) : (
          <button onClick={() => { onSelectNonStriker && onSelectNonStriker(); }} className="v222-select-btn v222-select-nonstriker">
            <span>Select Non-Striker</span>
            <span className="v222-arrow">→</span>
          </button>
        )}
      </div>

      {/* Bowler */}
      <div className={`v222-player-row v222-bowler-row ${waitingForNewBowler ? "v222-glow-red" : ""}`}>
        <div className="v222-player-label">
          <span className="v222-bowler-icon">⚾</span>
          <span className="v222-bowler-tag">Bowler</span>
          {bowler && !waitingForNewBowler && (
            <button onClick={onChangeBowler} className="v222-link-btn v222-link-right">Change</button>
          )}
        </div>
        {!inning.completed && (waitingForNewBowler || !bowler) ? (
          <button onClick={onSelectNewBowler} className="v222-select-btn v222-select-bowler">
            <span>Select New Bowler</span>
            <span className="v222-arrow">→</span>
          </button>
        ) : !bowler ? (
          <div className="v222-bowler-done">Innings complete</div>
        ) : (
          <div className="v222-player-info">
            <div className="v222-player-name-row">
              <span className="v222-player-name">{bowler.name}</span>
              {bowler.isCaptain && <span className="v222-badge v222-badge-c">C</span>}
              {bowler.isKeeper && <span className="v222-badge v222-badge-wk">WK</span>}
            </div>
            {(() => {
              const stats = getBowlerStats(inning, bowler.id, ballsPerOver);
              return (
                <div className="v222-player-stats">
                  <span><b>{stats.overs}</b><i>O</i></span>
                  <span><b>{stats.maidens}</b><i>M</i></span>
                  <span><b>{stats.runs}</b><i>R</i></span>
                  <span><b>{stats.wickets}</b><i>W</i></span>
                  <span><b>{stats.economy}</b><i>Econ</i></span>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      <Styles />
    </div>
  );
};

function Styles() {
  return (
    <style>{`
      .v222-players {
        background: linear-gradient(180deg, rgba(245,248,246,0.97) 0%, rgba(235,242,239,0.95) 100%);
        border: 1px solid rgba(16,185,129,0.15);
        border-radius: 1rem;
        padding: 0.625rem 0.75rem 0.75rem;
        box-shadow: 0 4px 16px rgba(0,0,0,0.18);
        color: #0f172a;
      }
      .v222-players-head {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
      }
      .v222-dot {
        width: 4px; height: 14px;
        background: #059669;
        border-radius: 999px;
      }
      .v222-section-label {
        font-size: 0.625rem;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-weight: 700;
        color: #047857;
      }
      .v222-undo-change {
        margin-left: auto;
        font-size: 0.625rem;
        color: #d97706;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        background: none; border: none; cursor: pointer;
      }
      .v222-undo-change:hover { color: #b45309; }
      .v222-empty {
        text-align: center;
        padding: 1.25rem 0;
        color: #64748b;
        font-size: 0.8125rem;
      }
      .v222-player-row {
        background: rgba(255,255,255,0.6);
        border-radius: 0.5rem;
        padding: 0.375rem 0.5rem;
        border: 1px solid rgba(0,0,0,0.05);
        margin-bottom: 0.375rem;
      }
      .v222-player-row:last-child { margin-bottom: 0; }
      .v222-striker-row { border-left: 3px solid #059669; }
      .v222-nonstriker-row { border-left: 3px solid #94a3b8; }
      .v222-bowler-row { border-left: 3px solid #dc2626; }
      .v222-player-label {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        margin-bottom: 0.125rem;
      }
      .v222-striker-icon { color: #059669; font-size: 0.5rem; }
      .v222-nonstriker-icon { color: #94a3b8; font-size: 0.5rem; }
      .v222-bowler-icon { font-size: 0.5rem; }
      .v222-striker-tag { font-size: 0.5625rem; font-weight: 700; color: #059669; text-transform: uppercase; letter-spacing: 0.06em; }
      .v222-nonstriker-tag { font-size: 0.5625rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.06em; }
      .v222-bowler-tag { font-size: 0.5625rem; font-weight: 700; color: #dc2626; text-transform: uppercase; letter-spacing: 0.06em; }
      .v222-player-info { flex: 1; }
      .v222-player-name-row {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        margin-bottom: 0.125rem;
      }
      .v222-player-name { font-weight: 700; font-size: 0.8125rem; color: #0f172a; }
      .v222-badge { font-size: 0.5rem; padding: 0.0625rem 0.25rem; border-radius: 0.25rem; font-weight: 700; }
      .v222-badge-c { background: #f59e0b; color: #fff; }
      .v222-badge-wk { background: #0284c7; color: #fff; }
      .v222-player-actions { margin-left: auto; display: flex; gap: 0.5rem; }
      .v222-link-btn {
        font-size: 0.5625rem; font-weight: 600; color: #2563eb; background: none; border: none;
        cursor: pointer; text-transform: uppercase; letter-spacing: 0.04em;
      }
      .v222-link-btn:hover { color: #1d4ed8; }
      .v222-retire-btn { color: #ea580c; }
      .v222-retire-btn:hover { color: #c2410c; }
      .v222-link-right { margin-left: auto; }
      .v222-player-stats {
        display: flex;
        gap: 0.625rem;
        font-size: 0.6875rem;
        color: #475569;
      }
      .v222-player-stats span { display: flex; align-items: baseline; gap: 0.125rem; }
      .v222-player-stats b { font-weight: 700; color: #0f172a; }
      .v222-player-stats i { font-style: normal; font-size: 0.5rem; color: #94a3b8; }
      .v222-select-btn {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 0.375rem 0.5rem;
        border-radius: 0.375rem;
        font-weight: 700;
        font-size: 0.75rem;
        border: 2px dashed;
        cursor: pointer;
        transition: all 0.15s ease;
      }
      .v222-select-striker { background: rgba(16,185,129,0.08); border-color: rgba(16,185,129,0.4); color: #047857; }
      .v222-select-striker:hover { background: rgba(16,185,129,0.14); }
      .v222-select-nonstriker { background: rgba(100,116,139,0.08); border-color: rgba(100,116,139,0.4); color: #475569; }
      .v222-select-nonstriker:hover { background: rgba(100,116,139,0.14); }
      .v222-select-bowler { background: rgba(220,38,38,0.08); border-color: rgba(220,38,38,0.4); color: #b91c1c; }
      .v222-select-bowler:hover { background: rgba(220,38,38,0.14); }
      .v222-arrow { font-size: 0.875rem; }
      .v222-bowler-done { font-size: 0.6875rem; color: #94a3b8; padding: 0.25rem 0; }
      .v222-glow-red {
        border-color: rgba(220,38,38,0.4) !important;
        animation: v222-glow 2s ease-in-out infinite;
      }
      @keyframes v222-glow {
        0%,100% { box-shadow: 0 0 6px rgba(220,38,38,0.2); }
        50% { box-shadow: 0 0 14px rgba(220,38,38,0.4); }
      }
    `}</style>
  );
}

export default CurrentPlayersCard;
